// Currencies WayForPay and LiqPay accept, in builder picker order:
// UAH first (default), then EUR and USD, then the rest alphabetically.
export const CURRENCY_CODES = [
  'UAH', 'EUR', 'USD',
  'AED', 'AMD', 'AUD', 'AZN', 'BDT', 'BGN', 'BRL', 'BYN', 'CAD', 'CHF', 'CNY', 'CZK',
  'DKK', 'DOP', 'DZD', 'EGP', 'GBP', 'GEL', 'HKD', 'HUF', 'IDR', 'ILS', 'INR', 'IQD',
  'IRR', 'JPY', 'KGS', 'KRW', 'KZT', 'LBP', 'LYD', 'MAD', 'MDL', 'MXN', 'MYR', 'NOK',
  'NZD', 'PKR', 'PLN', 'RON', 'RSD', 'SAR', 'SEK', 'SGD', 'THB', 'TJS', 'TMT', 'TND',
  'TRY', 'TWD', 'UZS', 'VND', 'ZAR',
] as const;

export type CurrencyCode = (typeof CURRENCY_CODES)[number];
export const DEFAULT_CURRENCY: CurrencyCode = 'UAH';

const CODE_SET = new Set<string>(CURRENCY_CODES);

export function isCurrencyCode(v: unknown): v is CurrencyCode {
  return typeof v === 'string' && CODE_SET.has(v);
}

// Legacy rows hold 'грн' (32 tickets before the 2026-10-13 cleanup); anything
// unknown falls back to UAH so display and gateways never see a bad code.
export function normalizeCurrency(v: string | null | undefined): CurrencyCode {
  const up = (v ?? '').trim().toUpperCase();
  if (up === 'ГРН') return 'UAH';
  return isCurrencyCode(up) ? up : DEFAULT_CURRENCY;
}

export function minorUnits(code: CurrencyCode): number {
  // Capped at 2: Intl reports 3 for LYD/TND, but gateway support for three
  // decimals is unverified.
  return Math.min(2, new Intl.NumberFormat('en', { style: 'currency', currency: code })
    .resolvedOptions().maximumFractionDigits ?? 2);
}

export function roundToMinor(amount: number, currency: string | null | undefined): number {
  const factor = 10 ** minorUnits(normalizeCurrency(currency));
  return Math.round((amount + Number.EPSILON) * factor) / factor;
}

export function currencyName(code: CurrencyCode, lang: 'uk' | 'en'): string {
  try {
    const name = new Intl.DisplayNames([lang === 'uk' ? 'uk' : 'en'], { type: 'currency' }).of(code);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : code;
  } catch {
    return code;
  }
}
